import { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { GSIcon } from 'src/components/shared/GSIcon'
import { collectedDataStepIconsMap } from 'src/constants/collectedData'
import { customerComplianceStepIcons } from 'src/constants/customer/icons'
import { customerComplianceStepTitles } from 'src/constants/customer/constants'
import { getCollectDataStepTitle } from 'src/utils/collectedData'
import { ContractorStepDetailsCardStatusBadge } from './ContractorStepDetailsCardStatusBadge'
import {
  ContractorStepDetailsCardUsageVariant,
  type ContractorStepDetailsCardStatus,
} from '../types'
import type { CollectedData } from 'src/interfaces/collectedData'
import type { CustomerCollectedDataTypeExtended } from 'src/constants/customer/enums'

export interface ContractorStepDetailsCardHeaderProps {
  usageVariant: ContractorStepDetailsCardUsageVariant
  stepType?: CustomerCollectedDataTypeExtended
  collectedData?: CollectedData | null
  status: ContractorStepDetailsCardStatus
  isRecollecting?: boolean
}

const ContractorStepDetailsCardHeaderComponent = ({
  usageVariant,
  stepType,
  collectedData,
  status,
  isRecollecting = false,
}: ContractorStepDetailsCardHeaderProps) => {
  const { title, icon } = useMemo(() => {
    const isMasterContractor =
      usageVariant === ContractorStepDetailsCardUsageVariant.MasterContractor && stepType != null

    if (isMasterContractor) {
      return {
        title: customerComplianceStepTitles[stepType],
        icon: customerComplianceStepIcons[stepType],
      }
    }

    if (collectedData != null) {
      return {
        title: getCollectDataStepTitle(collectedData),
        icon: collectedDataStepIconsMap[collectedData.collectDataStep.type],
      }
    }

    return { title: '', icon: null }
  }, [usageVariant, stepType, collectedData])

  return (
    <div css={tw`flex flex-row items-center justify-between flex-wrap gap-2`}>
      <div css={tw`flex flex-row items-center min-w-0 flex-1`}>
        {icon && <div css={tw`flex flex-shrink-0 items-center`}>{icon}</div>}
        <div css={tw`flex flex-col justify-center min-w-0 ml-2`}>
          <Typography variant="body2" fontWeight={700} css={tw`m-0`}>
            {title}
          </Typography>
        </div>
      </div>
      <div css={tw`flex flex-row items-center gap-2 flex-shrink-0`}>
        {isRecollecting && (
          <div css={tw`flex flex-shrink-0 items-center`}>
            <GSIcon iconName="gs_arrows_repeat_right_left" width={20} />
          </div>
        )}
        <ContractorStepDetailsCardStatusBadge status={status} />
      </div>
    </div>
  )
}

export const ContractorStepDetailsCardHeader = memo(ContractorStepDetailsCardHeaderComponent)
